import React, {
  createContext,
  useContext,
  useState,
  useEffect
} from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { common } from '@mui/material/colors';
import PropTypes from 'prop-types';
import shadows from 'src/theme/shadows';
import typography from 'src/theme/typography';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
        // Light theme colors
        background: {
          default: '#f5f5f5',
          paper: common.white,
          main: 'linear-gradient(180deg, rgb(66, 66, 74), rgb(25, 25, 25))',
        },
        primary: {
          contrastText: '#FEFEFE',
          main: '#0B6E99'
        },
        secondary: {
          main: '#2b2d42'
        },
        text: {
          primary: '#172b4d',
          secondary: '#6b778c'
        },
        success: {
          main: '#16CB77',
          secondary: '#14C990'
        },
      }
      : {
        // Dark theme colors
        background: {
          default: '#121212',
          paper: '#1e1e1e',
          main: 'linear-gradient(180deg, rgb(45, 45, 55), rgb(15, 15, 15))',
        },
        primary: {
          contrastText: '#FEFEFE',
          main: '#90caf9',
        },
        secondary: {
          main: '#424242',
        },
        text: {
          primary: '#ffffff',
          secondary: '#aaaaaa',
        },
        success: {
          main: '#4caf50',
          secondary: '#66bb6a'
        },
      }),
  },
  shadows,
  typography,
});

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Check localStorage for saved theme preference
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  const theme = getTheme(mode);

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const value = {
    mode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

CustomThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeContext;
